import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, BriefcaseIcon, DropletsIcon, InfinityIcon, GiftIcon, GlassesIcon, PlugIcon, ZapIcon } from 'lucide-react';
import { includedGear, GearItem } from '../data/product';
import { useTrackOnView } from '../hooks/useTrackOnView';
const icons = [ZapIcon, GlassesIcon, BriefcaseIcon, DropletsIcon, PlugIcon, BookOpenIcon, InfinityIcon, GiftIcon];

/**
 * The one visual value stack: what physically ships with each system. Real
 * photos drop into `includedGear[].image`; until then tiles show a neutral
 * icon rather than a generic stand-in product that is not ours.
 */
function GearVisual({
  item,
  Icon
}: {
  item: GearItem;
  Icon: React.ComponentType<{
    className?: string;
  }>;
}) {
  if (item.image) {
    return <div className="flex aspect-[4/3] w-full items-center justify-center border border-white/10 bg-ink-900" data-id="element-590" data-mp="3q9eqwtlkugum">
        <img src={item.image} alt={item.name} className="max-h-full w-full object-contain" loading="lazy" data-id="element-591" data-mp="mmec0trf3xlf" />
      </div>;
  }
  const isMystery = /mystery/i.test(item.name);
  return <div className={`flex aspect-[4/3] w-full items-center justify-center border ${isMystery ? 'border-grit-500/40 bg-ink-900' : 'border-white/10 bg-ink-900'}`} aria-hidden="true" data-id="element-592" data-mp="1p9qsxqy4cby2">
      {isMystery ? <span className="font-display text-6xl font-extrabold leading-none text-grit-500" data-id="element-593" data-mp="1uaxv9ahhsixc">?</span> : <Icon className="h-12 w-12 text-steel-500" data-id="element-594" data-mp="28z120m3czyn7" />}
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-595" data-mp="2hzpjjqryoh4u">
      <div className="border-b border-white/10 px-5 py-5 sm:px-7" data-id="element-596" data-mp="2czusnn86rrjs">
        <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-597" data-mp="nn9h712adn59">
          Everything in the Complete System.
        </h3>
        <p className="mt-2 text-[15px] text-steel-400" data-id="element-598" data-mp="2is0i8yb7fag7">
          In every Complete System and multipack, the case, pouch and cloth come included. Each system ships organized
          inside its hard case.
        </p>
      </div>

      <ul className="grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-599" data-mp="2wpfcqhgbe8f1">
        {includedGear.map((item, index) => {
        const Icon = icons[index] ?? GiftIcon;
        const isWarranty = /warranty/i.test(item.name);
        const body = <>
              <GearVisual item={item} Icon={Icon} data-id="element-600" data-mp="36li9t8wb7gv4" />
              <span className="mt-4 flex items-start justify-between gap-3" data-id="element-601" data-mp="3bfmko38p491h">
                <span className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-602" data-mp="3ge64ny3qbar7">
                  {item.name}
                </span>
                <span className={`shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${isWarranty ? 'gold-plate text-ink-950' : 'bg-grit-500 text-ink-950'}`} data-id="element-603" data-mp="3kdezz7ef3gov">
                  {item.tag}
                </span>
              </span>
              <span className="mt-1.5 block text-[14px] leading-relaxed text-steel-300" data-id="element-604" data-mp="14psxoou7um5y">{item.note}</span>
              <span className="mt-auto block pt-3 text-[11px] font-bold uppercase tracking-[0.16em] text-steel-500" data-id="element-605" data-mp="zrriwg3tp90a">
                {item.qty}
              </span>
            </>;
        return <li key={item.name} className="bg-ink-950" data-id="element-606" data-mp="1sgyd52w8vcfq" data-mpi={index}>
              {isWarranty ? <Link to="/legal/guarantee" className="flex h-full flex-col p-5 transition-colors duration-150 ease-grit hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c] sm:p-6" data-id="element-607" data-mp="1nodm294q3dfw">
                  {body}
                </Link> : <div className="flex h-full flex-col p-5 sm:p-6" data-id="element-608" data-mp="2bdgca5dfaem0">{body}</div>}
            </li>;
      })}
      </ul>
    </section>;
}